import { type ReactNode, useEffect, useState } from 'react'

/**
 * Bottom-sheet primitive — handles mount/unmount with slide-up/down +
 * backdrop fade, Escape-to-close, backdrop tap, and body-scroll lock.
 * Consumers only provide the body content and any per-instance styling
 * via `panelClassName` / `backdropClassName`.
 *
 * Animation contract:
 *   - opens: mount → first paint with translate-y-full → next frame
 *     transitions to translate-y-0 + backdrop fades to full opacity
 *   - closes: backdrop fades + panel translates back to translate-y-full,
 *     DOM unmounts after the animation completes (~220 ms)
 *
 * Two-phase visibility (`mounted` separate from `animateOpen`) is what
 * lets the close animation play before unmount. The double-state read
 * is intentional — using a single boolean would require unmounting on
 * close=true, which kills the slide-out animation.
 *
 * Used by: leaderboard DetailSheet, score SubmitSheet + HighScoreSheet,
 * poker StackEditSheet. Default styling (z-[60], bg-paper, rounded-t-2xl)
 * matches the most common consumer; overrides via the className props
 * cover the rest.
 */
const ANIM_MS = 220

const BottomSheet = ({
  open,
  onClose,
  ariaLabel,
  panelClassName = 'max-w-md',
  backdropClassName = 'bg-black/40',
  showGrabHandle = false,
  children,
}: {
  open: boolean
  onClose: () => void
  ariaLabel?: string
  /** Tailwind classes on the sliding panel — sizing/padding/layout. */
  panelClassName?: string
  /** Tailwind classes on the backdrop — default is a soft black/40. */
  backdropClassName?: string
  /** Grab-handle pill at the top of the panel (mobile sheet convention). */
  showGrabHandle?: boolean
  children: ReactNode
}) => {
  const [mounted, setMounted] = useState(open)
  const [animateOpen, setAnimateOpen] = useState(false)

  // Phase 1 — mount on open=true; schedule unmount on open=false after
  // the close animation finishes.
  useEffect(() => {
    if (open) {
      setMounted(true)
      return undefined
    }
    setAnimateOpen(false)
    const t = window.setTimeout(() => setMounted(false), ANIM_MS)
    return () => window.clearTimeout(t)
  }, [open])

  // Phase 2 — once mounted AND open, wait a frame so the initial render
  // pins at translate-y-full, then flip animateOpen so the CSS
  // transition fires. Without the RAF the browser collapses both
  // states into a single paint and the slide-up doesn't happen.
  useEffect(() => {
    if (!mounted || !open) return undefined
    const id = window.requestAnimationFrame(() => setAnimateOpen(true))
    return () => window.cancelAnimationFrame(id)
  }, [mounted, open])

  // Escape-to-close + body scroll lock — active for the entire mounted
  // window so users can dismiss even mid-animation.
  useEffect(() => {
    if (!mounted) return undefined
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = prevOverflow
    }
  }, [mounted, onClose])

  if (!mounted) return null

  return (
    <div className="fixed inset-0 z-[60] flex items-end justify-center">
      <button
        type="button"
        aria-label="Lukk"
        onClick={onClose}
        className={`absolute inset-0 cursor-default transition-opacity duration-200 ${backdropClassName} ${
          animateOpen ? 'opacity-100' : 'opacity-0'
        }`}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
        // `max-h-[90dvh]` is the default cap so a long body doesn't
        // push past the top of the screen. Consumers that need
        // internal scrolling (DetailSheet) ALSO set the panel itself
        // to `overflow-y-auto` and put a sticky header inside —
        // single-layer scroll is the iOS-Safari-friendly recipe.
        //
        // No default `bg-*` — the consumer picks the canvas. App
        // language puts cards (bg-paper) on a cream canvas
        // (bg-cream-bg) and the leaderboard detail sheet wants the
        // latter; the simple sheets (StackEditSheet, SubmitSheet,
        // HighScoreSheet) want the former.
        className={`relative w-full rounded-t-2xl shadow-xl max-h-[90dvh] transition-transform duration-200 ease-out ${
          animateOpen ? 'translate-y-0' : 'translate-y-full'
        } ${panelClassName}`}
      >
        {showGrabHandle ? (
          <div className="flex justify-center pt-3">
            <span
              aria-hidden
              className="block w-10 h-1.5 rounded-full bg-ink-200"
            />
          </div>
        ) : null}
        {children}
      </div>
    </div>
  )
}

export default BottomSheet
